import React, { useEffect, useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Spinner from '@dashboard/components/Spinner/Spinner';

const AutoSave = ({ debounceMs = 0, showState = true }) => {
  const formik = useFormikContext();

  const [initialHasChanges, setInitialHasChanges] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // we need to track our own history because we don't reset initialValues
  // which means that on cmd z, formik.values will be the same as initialValues
  // which in turn prevents form from being submitted
  const [valuesHistory, setValuesHistory] = useState([]);

  useEffect(() => {
    setValuesHistory(prev => [...prev, formik.values]);
  }, [formik.values]);

  useEffect(() => {
    if (valuesHistory.length > 1) {
      setHasChanges(true);
    }
  }, [valuesHistory]);

  const debouncedSubmit = useCallback(
    debounce(async () => {
      return formik.submitForm().then(success => {
        if (success) {
          setHasChanges(false); // reset local state after form is submitted successfully
        }
      });
    }, debounceMs),
    [formik.submitForm, debounceMs],
  );

  useEffect(() => {
    // Instead of formik.dirty, use the local hasChanges state
    if (hasChanges) {
      debouncedSubmit();
    }

    // Cleanup the debounce on unmount to prevent late invocations
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit, hasChanges, formik.values]);

  useEffect(() => {
    if (hasChanges) {
      setInitialHasChanges(true);
    }
  }, [hasChanges]);

  if (!showState) return null;

  if (!initialHasChanges) return null;

  // Show the saving state
  if (formik.isSubmitting) {
    return (
      <div className="flex justify-end items-center text-gray-500">
        <Spinner size="small" />
        <span className="ml-2">Saving changes</span>
      </div>
    );
  }

  // Show the saved state
  if (!hasChanges && !formik.isSubmitting) {
    return (
      <div className="flex justify-end items-center text-gray-500">
        <CheckCircleIcon className="h-5 w-5 text-green-300" />
        <span className="ml-2">Changes saved</span>
      </div>
    );
  }

  // Show the unsaved state
  if (hasChanges || !formik.isValid) {
    return (
      <div className="flex justify-end items-center text-gray-500">
        <>Changes not saved</>
      </div>
    );
  }

  return null;
};

export default AutoSave;
